import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { PackingItem, TripDTO } from '@almanac/shared';
import { DocumentEditDrawer } from '../components/DocumentEditDrawer.js';
import { DocumentListItem } from '../components/DocumentListItem.js';
import { DocumentPreview } from '../components/DocumentPreview.js';
import { EditDrawer } from '../components/EditDrawer.js';
import { LoadStatus } from '../components/LoadStatus.js';
import { ObligationListItem } from '../components/ObligationListItem.js';
import { ObligationPreview } from '../components/ObligationPreview.js';
import { api } from '../lib/api.js';
import { useDocuments, useUpdateDocument } from '../lib/documents.js';
import {
  useCreateObligation,
  useLifeAreas,
  useNowMinute,
  useObligations,
  useUpdateObligation,
} from '../lib/obligations.js';
import {
  addPackingItem,
  linkSheetGroups,
  mergePackingLabels,
  packingTemplateFrom,
  removePackingItem,
  splitTrips,
  starterTaskDrafts,
  togglePackingItem,
  tripDatesLabel,
  tripDocuments,
  tripPhase,
  tripReadiness,
  tripTasks,
  tripWhenLabel,
  useCreateTrip,
  useDeleteTrip,
  useTrips,
  useUpdateTrip,
  vaultIdentityDocs,
  walletGroups,
} from '../lib/travel.js';

/**
 * Travel — each trip as a container of tasks, documents, dates, and a
 * packing checklist (PROJECT.md §Travel; approved container-first
 * mockup with the readiness bar). The index leads with the next trip
 * as a hero; a trip page lists its linked obligations and documents
 * (both stay owned by their own tools) and the packing list.
 */

type TripSheetState = { kind: 'new' } | { kind: 'edit'; tripId: string } | null;

// ── Index ───────────────────────────────────────────────────────────

export function TravelPage() {
  const trips = useTrips();
  const obligations = useObligations();
  const documents = useDocuments();
  const navigate = useNavigate();
  const nowMs = useNowMinute();
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const [sheet, setSheet] = useState<TripSheetState>(null);
  const shelf = useMemo(() => splitTrips(trips.data ?? [], now), [trips.data, now]);
  const next = shelf.next;
  const readiness = next ? tripReadiness(next, obligations.data ?? []) : null;

  return (
    <section>
      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label="Back to home"
            onClick={() => navigate('/')}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">Trips</div>
            <h2 className="page-header__title">Travel</h2>
          </div>
        </div>
        <button
          type="button"
          className="page-header__icon-button"
          aria-label="Add a trip"
          onClick={() => setSheet({ kind: 'new' })}
        >
          <PlusIcon />
        </button>
      </header>

      <LoadStatus isLoading={trips.isLoading} isError={trips.isError} error={trips.error} />

      <div className="page-measure">
        {trips.isSuccess && (trips.data ?? []).length === 0 && (
          <p className="empty-state">
            No trips yet. Add one and it becomes the container for its tasks, bookings,
            tickets, and packing list.
          </p>
        )}

        {next && (
          <Link to={`/travel/${next.id}`} className="trip-hero">
            <div className="trip-hero__label">
              {tripPhase(next, now) === 'current'
                ? 'Underway'
                : `Up next · ${tripWhenLabel(next, now)}`}
            </div>
            <div className="trip-hero__title">{next.title}</div>
            <div className="trip-hero__dates">{tripDatesLabel(next)}</div>
            {readiness && (
              <div className="trip-ready">
                <div className="trip-ready__line">
                  <span>Ready</span>
                  <strong>
                    {readiness.done} of {readiness.total}
                  </strong>
                </div>
                <ReadyTrack done={readiness.done} total={readiness.total} />
              </div>
            )}
            <div className="trip-hero__meta">
              <span className="trip-hero__stat">
                <strong>{tripTasks(obligations.data ?? [], next.id).length}</strong> tasks open
              </span>
              <span className="trip-hero__stat">
                <strong>{tripDocuments(documents.data ?? [], next.id).length}</strong> documents
              </span>
              <span className="trip-hero__stat">
                <strong>
                  {next.packing.filter((p) => p.done).length}/{next.packing.length}
                </strong>{' '}
                packed
              </span>
            </div>
          </Link>
        )}

        {shelf.upcoming.length > 0 && (
          <>
            <div className="doc-seclabel">Upcoming</div>
            <div className="doc-folders">
              {shelf.upcoming.map((t) => (
                <TripRow key={t.id} trip={t} now={now} />
              ))}
            </div>
          </>
        )}

        {shelf.past.length > 0 && (
          <>
            <div className="doc-seclabel">Past</div>
            <div className="doc-folders">
              {shelf.past.map((t) => (
                <TripRow key={t.id} trip={t} now={now} />
              ))}
            </div>
          </>
        )}

        {(trips.data ?? []).length > 0 && (
          <button type="button" className="documents-add" onClick={() => setSheet({ kind: 'new' })}>
            <span className="documents-add__plus" aria-hidden>
              +
            </span>
            Add a trip
          </button>
        )}
      </div>

      {sheet && (
        <TripSheet
          state={sheet}
          trips={trips.data ?? []}
          now={now}
          onClose={() => setSheet(null)}
          onCreated={(id) => navigate(`/travel/${id}`)}
        />
      )}
    </section>
  );
}

function TripRow({ trip, now }: { trip: TripDTO; now: Date }) {
  const days =
    trip.endDate && trip.endDate !== trip.startDate
      ? `${
          Math.round(
            (new Date(`${trip.endDate}T00:00:00`).getTime() -
              new Date(`${trip.startDate}T00:00:00`).getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1
        } days`
      : null;
  const sub = [trip.destination, days].filter(Boolean).join(' · ');
  return (
    <Link to={`/travel/${trip.id}`} className="trip-row">
      <span className="trip-row__chip" aria-hidden>
        <PlaneIcon />
      </span>
      <span className="trip-row__mid">
        <span className="trip-row__name">{trip.title}</span>
        {sub && <span className="trip-row__sub">{sub}</span>}
      </span>
      <span className="trip-row__when">{tripWhenLabel(trip, now)}</span>
    </Link>
  );
}

function ReadyTrack({ done, total }: { done: number; total: number }) {
  return (
    <div className="trip-ready__track" aria-hidden>
      <div
        className="trip-ready__fill"
        style={{ width: `${total > 0 ? Math.round((done / total) * 100) : 0}%` }}
      />
    </div>
  );
}

// ── Trip page ───────────────────────────────────────────────────────

export function TripPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const trips = useTrips();
  const obligations = useObligations();
  const documents = useDocuments();
  const lifeAreas = useLifeAreas();
  const navigate = useNavigate();
  const nowMs = useNowMinute();
  const now = useMemo(() => new Date(nowMs), [nowMs]);
  const updateTrip = useUpdateTrip();
  const createObligation = useCreateObligation();
  const updateObligation = useUpdateObligation();
  const updateDocument = useUpdateDocument();

  const trip = (trips.data ?? []).find((t) => t.id === tripId) ?? null;
  const tasks = useMemo(
    () => (trip ? tripTasks(obligations.data ?? [], trip.id) : []),
    [obligations.data, trip],
  );
  const docs = useMemo(
    () => (trip ? tripDocuments(documents.data ?? [], trip.id) : []),
    [documents.data, trip],
  );
  const wallet = useMemo(() => walletGroups(docs), [docs]);
  const tripActive = trip ? tripPhase(trip, now) !== 'past' : false;
  // Identity records ride along read-only from the Vault while the
  // trip is live — no linking needed, they're already kept offline.
  const identity = useMemo(
    () => (tripActive ? vaultIdentityDocs(documents.data ?? []) : []),
    [documents.data, tripActive],
  );
  const readiness = trip ? tripReadiness(trip, obligations.data ?? []) : null;
  const areasData = lifeAreas.data;
  const areaById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof areasData>[number]>();
    for (const a of areasData ?? []) map.set(a.id, a);
    return map;
  }, [areasData]);
  // New quick tasks file under the Travel life area when one exists.
  const travelAreaId =
    (areasData ?? []).find((a) => a.slug === 'travel' || /travel/i.test(a.name))?.id ??
    (areasData ?? [])[0]?.id;

  const [sheet, setSheet] = useState<TripSheetState>(null);
  const [linking, setLinking] = useState<'tasks' | 'documents' | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [docPreviewId, setDocPreviewId] = useState<string | null>(null);
  const [docEditingId, setDocEditingId] = useState<string | null>(null);
  const [docAdding, setDocAdding] = useState(false);
  const [taskDraft, setTaskDraft] = useState<string | null>(null); // null = collapsed
  const [packDraft, setPackDraft] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(false);

  const previewing = obligations.data?.find((o) => o.id === previewingId) ?? null;
  const editing = obligations.data?.find((o) => o.id === editingId) ?? null;

  function savePacking(packing: PackingItem[]) {
    if (!trip) return;
    updateTrip.mutate({ id: trip.id, input: { packing } });
  }

  function addQuickTask() {
    const title = taskDraft?.trim();
    if (!title || !trip || !travelAreaId) return;
    createObligation.mutate({
      id: crypto.randomUUID(),
      title,
      description: null,
      lifeAreaId: travelAreaId,
      dueDate: trip.startDate,
      recurrenceRule: null,
      priority: 'medium',
      reminderDaysBefore: null,
      notes: null,
      visibility: trip.visibility,
      kind: 'task',
      occasionType: null,
      occasionLabel: null,
      anchorYear: null,
      personIds: [],
      tripId: trip.id,
    });
    setTaskDraft('');
  }

  async function suggestPackingList() {
    if (!trip || suggesting) return;
    setSuggesting(true);
    setSuggestError(false);
    try {
      const labels = await api.suggestPacking({
        title: trip.title,
        destination: trip.destination,
        startDate: trip.startDate,
        endDate: trip.endDate,
        notes: trip.notes,
      });
      savePacking(mergePackingLabels(trip.packing, labels));
    } catch {
      setSuggestError(true);
    } finally {
      setSuggesting(false);
    }
  }

  if (trips.isSuccess && !trip) {
    return (
      <section>
        <p className="empty-state">This trip no longer exists.</p>
      </section>
    );
  }

  return (
    <section>
      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label="Back to Travel"
            onClick={() => navigate('/travel')}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">Trip</div>
            <h2 className="page-header__title">{trip?.title ?? '…'}</h2>
          </div>
        </div>
        <button
          type="button"
          className="page-header__icon-button"
          aria-label="Edit this trip"
          onClick={() => trip && setSheet({ kind: 'edit', tripId: trip.id })}
        >
          <PencilIcon />
        </button>
      </header>

      <LoadStatus isLoading={trips.isLoading} isError={trips.isError} error={trips.error} />

      {trip && (
        <div className="page-measure">
          <p className="today-intro trip-intro">
            {tripDatesLabel(trip)} · {tripWhenLabel(trip, now)}
            {trip.destination ? ` · ${trip.destination}` : ''}
          </p>
          {readiness && (
            <div className="trip-ready trip-ready--page">
              <div className="trip-ready__line">
                <span>Ready</span>
                <strong>
                  {readiness.done} of {readiness.total}
                </strong>
              </div>
              <ReadyTrack done={readiness.done} total={readiness.total} />
            </div>
          )}

          {/* ── Tasks ─────────────────────────────────────────── */}
          {/* Every section is one surface card that always exists —
              rows when there are any, the add/link actions as quiet
              in-card rows below them (the Budget-card chassis). An
              empty trip is a set of warm cards inviting content, not
              floating labels. */}
          <div className="doc-seclabel">
            Tasks {tasks.length > 0 && <span className="doc-seclabel__n">· {tasks.length} open</span>}
          </div>
          <div className="list trip-card" role="list">
            {tasks.map((o) => (
              <ObligationListItem
                key={o.id}
                obligation={o}
                lifeArea={areaById.get(o.lifeAreaId)}
                hideLifeArea
                onEdit={setPreviewingId}
              />
            ))}
            {taskDraft === null ? (
              <>
                <button type="button" className="trip-action" onClick={() => setTaskDraft('')}>
                  + Add a task
                </button>
                <button type="button" className="trip-action" onClick={() => setLinking('tasks')}>
                  Link an existing task…
                </button>
              </>
            ) : (
              <div className="trip-quickadd">
                <input
                  className="trip-quickadd__input"
                  value={taskDraft}
                  placeholder="What needs doing before you go?"
                  autoFocus
                  onChange={(e) => setTaskDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addQuickTask();
                    if (e.key === 'Escape') setTaskDraft(null);
                  }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={addQuickTask}
                  disabled={!taskDraft.trim()}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  onClick={() => setTaskDraft(null)}
                >
                  Done
                </button>
              </div>
            )}
          </div>

          {/* ── Documents — the travel wallet ─────────────────── */}
          <div className="doc-seclabel">
            Documents {docs.length > 0 && <span className="doc-seclabel__n">· {docs.length}</span>}
          </div>
          <div className="doc-folders trip-card">
            {wallet.map((g) => (
              <Fragment key={g.key}>
                {(wallet.length > 1 || g.key !== 'other') && (
                  <div className="month-group__subheading trip-subhead">{g.label}</div>
                )}
                {g.rows.map((d) => (
                  <DocumentListItem
                    key={d.id}
                    document={d}
                    meta={[d.category, d.issuer].filter(Boolean).join(' · ')}
                    now={now}
                    onOpen={(x) => setDocPreviewId(x.id)}
                    trailing={
                      d.fileName && tripActive ? (
                        <span className="doc-row__flag doc-row__flag--offline">● offline</span>
                      ) : null
                    }
                  />
                ))}
              </Fragment>
            ))}
            {identity.length > 0 && (
              <>
                <div className="month-group__subheading trip-subhead">
                  Identity — from the Vault
                </div>
                {identity.map((d) => (
                  <DocumentListItem
                    key={d.id}
                    document={d}
                    meta={[
                      d.category,
                      d.expiryDate ? `valid to ${d.expiryDate.slice(0, 4)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    now={now}
                    onOpen={(x) => setDocPreviewId(x.id)}
                    trailing={<span className="doc-row__flag doc-row__flag--vault">vault</span>}
                  />
                ))}
              </>
            )}
            <button type="button" className="trip-action" onClick={() => setDocAdding(true)}>
              + Add a document
            </button>
            <button type="button" className="trip-action" onClick={() => setLinking('documents')}>
              Link existing documents…
            </button>
          </div>
          <p className="trip-hint">
            {docs.length === 0
              ? 'Add a booking here — file, AI read and all — or link one already in Documents. Linked files are kept on this device until the trip ends.'
              : 'Everything here is kept on this device until the trip ends.'}
            {identity.length > 0 && ' Identity records come along automatically from the Vault.'}
          </p>

          {/* ── Packing ───────────────────────────────────────── */}
          <div className="doc-seclabel">
            Packing{' '}
            {trip.packing.length > 0 && (
              <span className="doc-seclabel__n">
                · {trip.packing.filter((p) => p.done).length} of {trip.packing.length}
              </span>
            )}
          </div>
          <div className="doc-folders trip-card">
            {trip.packing.map((p) => (
              <div key={p.id} className="pack-row">
                <button
                  type="button"
                  className={`pack-row__check ${p.done ? 'pack-row__check--done' : ''}`}
                  aria-pressed={p.done}
                  aria-label={`Mark ${p.label} ${p.done ? 'unpacked' : 'packed'}`}
                  onClick={() => savePacking(togglePackingItem(trip.packing, p.id))}
                >
                  {p.done ? '✓' : ''}
                </button>
                <span className={`pack-row__name ${p.done ? 'pack-row__name--done' : ''}`}>
                  {p.label}
                </span>
                {p.qty && <span className="pack-row__qty">{p.qty}</span>}
                <button
                  type="button"
                  className="pack-row__remove"
                  aria-label={`Remove ${p.label}`}
                  onClick={() => savePacking(removePackingItem(trip.packing, p.id))}
                >
                  ×
                </button>
              </div>
            ))}
            <div className="trip-quickadd">
              <input
                className="trip-quickadd__input"
                value={packDraft}
                placeholder="Add to the packing list…"
                onChange={(e) => setPackDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && packDraft.trim()) {
                    savePacking(addPackingItem(trip.packing, packDraft));
                    setPackDraft('');
                  }
                }}
              />
              <button
                type="button"
                className="btn btn--secondary btn--small"
                disabled={!packDraft.trim()}
                onClick={() => {
                  savePacking(addPackingItem(trip.packing, packDraft));
                  setPackDraft('');
                }}
              >
                Add
              </button>
            </div>
            <button
              type="button"
              className="trip-action"
              onClick={() => void suggestPackingList()}
              disabled={suggesting}
            >
              {suggesting ? 'Thinking…' : '✦ Suggest a packing list'}
            </button>
          </div>
          {suggestError && (
            <p className="trip-hint">Suggestions aren&rsquo;t available right now — try again later.</p>
          )}
        </div>
      )}

      {sheet && trip && (
        <TripSheet
          state={sheet}
          trips={trips.data ?? []}
          now={now}
          onClose={() => setSheet(null)}
          onDeleted={() => navigate('/travel')}
        />
      )}

      {linking === 'tasks' && trip && (
        <LinkSheet
          title="Link tasks"
          searchPlaceholder="Search your tasks…"
          groupsFor={(q) => {
            const query = q.trim().toLowerCase();
            const items = (obligations.data ?? [])
              .filter((o) => o.status !== 'completed' && o.kind === 'task')
              .map((o) => ({
                id: o.id,
                label: o.title,
                sub: areaById.get(o.lifeAreaId)?.name ?? '',
                linked: o.tripId === trip.id,
              }))
              .filter((i) => !query || i.label.toLowerCase().includes(query));
            return items.length > 0 ? [{ label: `Tasks · ${items.length}`, items }] : [];
          }}
          onApply={(changes) => {
            for (const { id, linked } of changes) {
              updateObligation.mutate({ id, input: { tripId: linked ? trip.id : null } });
            }
          }}
          onClose={() => setLinking(null)}
        />
      )}
      {linking === 'documents' && trip && (
        <LinkSheet
          title="Link documents"
          searchPlaceholder="Search your documents…"
          groupsFor={(q) => {
            const g = linkSheetGroups(documents.data ?? [], trip, q);
            const toItem = (d: (typeof g.linked)[number]) => ({
              id: d.id,
              label: d.title,
              sub: [
                d.category,
                d.issuer,
                d.issuedDate ? d.issuedDate.slice(0, 7) : null,
              ]
                .filter(Boolean)
                .join(' · '),
              linked: d.tripId === trip.id,
            });
            return [
              { label: `Linked · ${g.linked.length}`, items: g.linked.map(toItem) },
              { label: 'Likely for this trip', items: g.likely.map(toItem) },
              { label: 'Everything else', items: g.rest.map(toItem) },
            ].filter((grp) => grp.items.length > 0);
          }}
          onApply={(changes) => {
            for (const { id, linked } of changes) {
              updateDocument.mutate({ id, input: { tripId: linked ? trip.id : null } });
            }
          }}
          onClose={() => setLinking(null)}
        />
      )}

      {previewing && (
        <ObligationPreview
          obligation={previewing}
          lifeArea={areaById.get(previewing.lifeAreaId)}
          lifeAreas={areasData ?? []}
          onClose={() => setPreviewingId(null)}
          onEdit={() => {
            const id = previewing.id;
            setPreviewingId(null);
            setEditingId(id);
          }}
        />
      )}
      <EditDrawer obligation={editing} onClose={() => setEditingId(null)} />

      {docPreviewId &&
        (() => {
          const doc = documents.data?.find((d) => d.id === docPreviewId);
          if (!doc) return null;
          return (
            <DocumentPreview
              document={doc}
              lifeAreaName={areaById.get(doc.lifeAreaId)?.name}
              onClose={() => setDocPreviewId(null)}
              onEdit={() => {
                setDocPreviewId(null);
                setDocEditingId(doc.id);
              }}
            />
          );
        })()}
      <DocumentEditDrawer
        target={
          docAdding
            ? 'new'
            : docEditingId
              ? (documents.data?.find((d) => d.id === docEditingId) ?? null)
              : null
        }
        presetTripId={trip?.id}
        onClose={() => {
          setDocAdding(false);
          setDocEditingId(null);
        }}
      />
    </section>
  );
}

// ── Trip sheet (create / edit) ──────────────────────────────────────

function TripSheet({
  state,
  trips,
  now,
  onClose,
  onCreated,
  onDeleted,
}: {
  state: NonNullable<TripSheetState>;
  trips: TripDTO[];
  now: Date;
  onClose: () => void;
  onCreated?: (id: string) => void;
  onDeleted?: () => void;
}) {
  const isNew = state.kind === 'new';
  const existing = state.kind === 'edit' ? (trips.find((t) => t.id === state.tripId) ?? null) : null;
  const createTrip = useCreateTrip();
  const updateTrip = useUpdateTrip();
  const deleteTrip = useDeleteTrip();
  const createObligation = useCreateObligation();
  const lifeAreas = useLifeAreas();

  const [title, setTitle] = useState(existing?.title ?? '');
  const [destination, setDestination] = useState(existing?.destination ?? '');
  const [startDate, setStartDate] = useState(existing?.startDate ?? '');
  const [endDate, setEndDate] = useState(existing?.endDate ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [shared, setShared] = useState(existing ? existing.visibility === 'shared' : true);
  // The Tripsy borrowing: standard pre-trip errands offered as chips
  // on creation; each picked one becomes a real linked obligation.
  const [starters, setStarters] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (state.kind === 'edit' && !existing) onClose();
  }, [state, existing, onClose]);

  const trimmed = title.trim();
  const canSave = trimmed.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(startDate);
  const drafts = canSave && isNew ? starterTaskDrafts(trimmed, startDate, now) : [];
  const travelAreaId =
    (lifeAreas.data ?? []).find((a) => a.slug === 'travel' || /travel/i.test(a.name))?.id ??
    (lifeAreas.data ?? [])[0]?.id;

  function save() {
    if (!canSave) return;
    const visibility = shared ? ('shared' as const) : ('private' as const);
    if (isNew) {
      const id = crypto.randomUUID();
      // The reusable template, quietly: a new trip starts from the
      // most recent trip's packing labels, unchecked.
      const packing = mergePackingLabels([], packingTemplateFrom(trips));
      createTrip.mutate({
        id,
        title: trimmed,
        destination: destination.trim() || null,
        startDate,
        endDate: endDate || null,
        notes: notes.trim() || null,
        packing,
        visibility,
      });
      if (travelAreaId) {
        for (const d of drafts.filter((d) => starters.has(d.key))) {
          createObligation.mutate({
            id: crypto.randomUUID(),
            title: d.title,
            description: null,
            lifeAreaId: travelAreaId,
            dueDate: d.dueDate,
            recurrenceRule: null,
            priority: 'medium',
            reminderDaysBefore: null,
            notes: null,
            visibility,
            kind: 'task',
            occasionType: null,
            occasionLabel: null,
            anchorYear: null,
            personIds: [],
            tripId: id,
          });
        }
      }
      onCreated?.(id);
    } else if (existing) {
      updateTrip.mutate({
        id: existing.id,
        input: {
          title: trimmed,
          destination: destination.trim() || null,
          startDate,
          endDate: endDate || null,
          notes: notes.trim() || null,
          visibility,
        },
      });
    }
    onClose();
  }

  function remove() {
    if (!existing) return;
    deleteTrip.mutate({ id: existing.id });
    onClose();
    onDeleted?.();
  }

  return (
    <SheetShell
      title={isNew ? 'Add a trip' : existing?.title || 'Edit trip'}
      label={isNew ? 'Add a trip' : 'Edit this trip'}
      onClose={onClose}
      footer={
        confirming && existing ? (
          <div className="drawer__confirm" role="alertdialog" aria-label="Confirm delete">
            <span className="drawer__confirm-message">
              Delete {existing.title}?
              <span className="drawer__confirm-detail">
                Its tasks and documents keep living in their own tools — only the container
                goes.
              </span>
            </span>
            <div className="drawer__confirm-actions">
              <button
                type="button"
                className="btn btn--secondary btn--small"
                onClick={() => setConfirming(false)}
                autoFocus
              >
                Keep
              </button>
              <button type="button" className="btn btn--primary btn--small" onClick={remove}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            {existing ? (
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setConfirming(true)}
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="drawer__footer-actions">
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={save}
                disabled={!canSave}
              >
                Save
              </button>
            </div>
          </>
        )
      }
    >
      <label className="field">
        <span className="field__label">Title</span>
        <input
          className="field__input"
          type="text"
          value={title}
          placeholder="e.g. Sydney & Canberra"
          autoFocus={isNew}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Destination</span>
        <input
          className="field__input"
          type="text"
          value={destination}
          placeholder="Optional — city or country"
          onChange={(e) => setDestination(e.target.value)}
        />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="field__label">From</span>
          <input
            className="field__input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">To</span>
          <input
            className="field__input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
      </div>
      <label className="field">
        <span className="field__label">Notes</span>
        <textarea
          className="field__input field__input--textarea"
          value={notes}
          rows={2}
          placeholder="Optional"
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">Who sees it</span>
        <select
          className="field__input field__input--select"
          value={shared ? 'shared' : 'private'}
          onChange={(e) => setShared(e.target.value === 'shared')}
        >
          <option value="shared">Shared with the household</option>
          <option value="private">Private — just me</option>
        </select>
      </label>

      {drafts.length > 0 && (
        <div className="field">
          <span className="field__label">Starter tasks</span>
          <div className="trip-starters">
            {drafts.map((d) => {
              const on = starters.has(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  className={`doc-chip ${on ? 'doc-chip--on' : ''}`}
                  aria-pressed={on}
                  onClick={() =>
                    setStarters((prev) => {
                      const next = new Set(prev);
                      if (next.has(d.key)) next.delete(d.key);
                      else next.add(d.key);
                      return next;
                    })
                  }
                >
                  {d.title.replace(` for ${trimmed}`, '').replace(` — ${trimmed}`, '')}
                </button>
              );
            })}
          </div>
          <span className="field__hint">
            The usual pre-trip errands, timed back from departure. Picked ones become real
            tasks linked to the trip.
          </span>
        </div>
      )}
    </SheetShell>
  );
}

// ── Link sheet (staged multi-toggle over tasks or documents) ────────

interface LinkItem {
  id: string;
  label: string;
  sub: string;
  linked: boolean;
}

interface LinkGroup {
  label: string;
  items: LinkItem[];
}

/** Search-first staged linker: the caller supplies `groupsFor(query)`
 *  so documents can shelve as Linked / Likely-for-this-trip /
 *  Everything-else while tasks stay one flat searchable list. Toggles
 *  stage locally; Save applies only the changes. */
function LinkSheet({
  title,
  searchPlaceholder,
  groupsFor,
  onApply,
  onClose,
}: {
  title: string;
  searchPlaceholder: string;
  groupsFor: (query: string) => LinkGroup[];
  onApply: (changes: { id: string; linked: boolean }[]) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  // Baseline (unfiltered) once, so Save can diff staged toggles even
  // for rows currently hidden by the search.
  const [baseline] = useState(() => {
    const map = new Map<string, boolean>();
    for (const g of groupsFor('')) for (const i of g.items) map.set(i.id, i.linked);
    return map;
  });
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set([...baseline.entries()].filter(([, on]) => on).map(([id]) => id)),
  );
  const groups = groupsFor(query);

  function apply() {
    const changes = [...baseline.entries()]
      .filter(([id, was]) => was !== selected.has(id))
      .map(([id]) => ({ id, linked: selected.has(id) }));
    if (changes.length > 0) onApply(changes);
    onClose();
  }

  return (
    <SheetShell
      title={title}
      label={title}
      onClose={onClose}
      footer={
        <>
          <span />
          <div className="drawer__footer-actions">
            <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn--primary btn--small" onClick={apply}>
              Save
            </button>
          </div>
        </>
      }
    >
      <div className="obligations-search linksheet-search">
        <input
          type="search"
          className="obligations-search__input"
          placeholder={searchPlaceholder}
          value={query}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== '' && (
          <button
            type="button"
            className="obligations-search__clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
      {groups.length === 0 && (
        <p className="trip-hint">
          {query.trim() ? `No matches for “${query.trim()}”.` : 'Nothing to link yet.'}
        </p>
      )}
      {groups.map((g) => (
        <Fragment key={g.label}>
          <div className="linksheet-group">{g.label}</div>
          {g.items.map((i) => {
            const on = selected.has(i.id);
            return (
              <button
                key={i.id}
                type="button"
                className={`linkrow ${on ? 'linkrow--on' : ''}`}
                aria-pressed={on}
                onClick={() =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(i.id)) next.delete(i.id);
                    else next.add(i.id);
                    return next;
                  })
                }
              >
                <span className="linkrow__mid">
                  <span className="linkrow__title">{i.label}</span>
                  {i.sub && <span className="linkrow__meta">{i.sub}</span>}
                </span>
                {on ? (
                  <svg
                    className="linkrow__check"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                ) : (
                  <span className="linkrow__check" aria-hidden />
                )}
              </button>
            );
          })}
        </Fragment>
      ))}
    </SheetShell>
  );
}

// ── Shared chassis + icons ──────────────────────────────────────────

function SheetShell({
  title,
  label,
  children,
  footer,
  onClose,
}: {
  title: string;
  label: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={label}>
        <div className="drawer__grip">
          <div className="drawer__handle" aria-hidden />
          <header className="drawer__header">
            <h2 className="drawer__title">{title}</h2>
          </header>
        </div>
        <div className="drawer__body">{children}</div>
        <footer className="drawer__footer">{footer}</footer>
      </aside>
    </>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
    </svg>
  );
}
